import type { Dispatch, FC, PropsWithChildren, RefObject } from 'react';
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { frontConfig } from '../config';
import type { VoterInstance } from '../thread/BottomActions/VoteBottomAction';
import type { ThreadMessageChildrenInstance } from '../thread/ThreadMessage/ThreadMessageNode';
import type { Author, Message } from '../types/api';
import { scrollBottomReached } from '../utils/dom';
import { isBrowser } from '../utils/fragments/isBrowser';

// Usage:
// 1. wrap all usages with <ScrollSectionsProvider> (both registration and watching scroll section changes),
// 2. Register DOM elements and their model to watch with useRegisterScrollSection(),
// 3. Where you want to show some content changing with the currently visible section, use useCurrentScrollSection.
//    It returns the current scroll section and re-render when it changes.

// Specific interface for a section to register. Depends on what the app needs to watch and display.

interface ScrollSection {
  id: string | undefined;
  author: Author | undefined;
  voteRef: VoteRef;
  nestedAnswersRef?: NestedAnswersRef;
  // Can be undefined when answering the topic directly.
  messageId: string | undefined;
  noBubbleFlow?: boolean;
}

export type VoteRef = RefObject<VoterInstance | null>;

// Generic code

type DomRef = RefObject<HTMLElement>;
type ModelRef = RefObject<ScrollSection>;
type NestedAnswersRef = RefObject<ThreadMessageChildrenInstance>;

interface DomAndModelRefs {
  domRef: DomRef;
  modelRef: ModelRef;
}

type SetCurrentScrollSection = Dispatch<React.SetStateAction<ScrollSection | undefined>>;
type ForceCurrentScrollSection = (messageId: string, messageToEdit?: Message) => void;

interface SectionsHelpers {
  sections: DomAndModelRefs[];
  setCurrentScrollSection: SetCurrentScrollSection;
  forceCurrentScrollSection: ForceCurrentScrollSection;
  unforceCurrentScrollSection: VoidFunction;
  checkCurrentScrollSection: VoidFunction;
}

const ScrollSectionsHelpersContext = createContext<SectionsHelpers>(null as any);
ScrollSectionsHelpersContext.displayName = 'ScrollSectionsHelpersContext';

// The current section, that changes often, is set in a separate context, to avoid useless renders of unrelated components (e.g. the ones that register sections or use helpers).
const CurrentScrollSectionContext = createContext<ScrollSection | undefined>(undefined);
CurrentScrollSectionContext.displayName = 'CurrentScrollSectionContext';

const CurrentScrollSectionForcedContext = createContext<boolean | Message>(false);
CurrentScrollSectionForcedContext.displayName = 'ForceCurrentScrollSectionContext';

// Use at the root of all sections that should be managed when scrolling
export const ScrollSectionsProvider: FC<PropsWithChildren<{}>> = memo(
  function ScrollSectionsProvider({ children }) {
    const sectionsRef = useRef<DomAndModelRefs[]>([]);
    const scrollableElementRef = useRef<Element | null>(null);

    const [currentScrollSection, setCurrentScrollSection] = useState<ScrollSection>();

    const [currentScrollSectionForced, setCurrentScrollSectionForced] = useState<boolean | Message>(
      false,
    );
    const currentScrollSectionForcedAsRef = useRef(currentScrollSectionForced);
    currentScrollSectionForcedAsRef.current = currentScrollSectionForced;
    const forceCurrentScrollSection = useCallback<ForceCurrentScrollSection>(
      (messageId, messageToEdit) => {
        let sections = sectionsRef.current;
        const section = sections?.find(s => s.modelRef.current?.id === messageId)?.modelRef.current;
        if (section) {
          setCurrentScrollSectionForced(messageToEdit || true);
          setCurrentScrollSection(section);
        }
      },
      [],
    );
    const unforceCurrentScrollSection = useCallback(() => {
      setCurrentScrollSectionForced(false);
    }, []);

    // Scroll handler: update the current section based on the scroll position. The current section is exposed as a context variable.
    const handleScroll = useCallback(() => {
      const scroller = scrollableElementRef.current;
      if (!scroller || currentScrollSectionForcedAsRef.current) return;

      let sections = sectionsRef.current;

      // Sort sections by vertical position - TBC if we really need to sort at each scroll check.
      // Ideally, sort when inserting sections, when they are registered. See insertInSortedArray().
      // But logs seemed to indicate that the order changes when scrolling (weird).
      sections = [...sections].sort(compareRef);

      // const scrollingElement: Element = scroller.scrollingElement; // If Document
      const scrollingElement: Element = scroller;

      // Special case: end of scroll reached, we focus the last section.
      if (!scrollingElement) {
        throw new Error('No scrolling element to check scroll bottom reached.');
      }
      if (scrollBottomReached(scrollingElement)) {
        const lastModel = sections[sections.length - 1]?.modelRef.current;
        if (lastModel) {
          setCurrentScrollSection(lastModel);
          return;
        }
      }

      if (!scrollingElement) {
        throw new Error('No scrolling element to check scroll bottom reached (2).');
      }
      const heightToFocusSection = scrollingElement.clientHeight * 0.6;

      // Normal case: the current section is the lowest section that is above 1/3 of the screen.
      for (let i = sections.length - 1; i > 0; i--) {
        const section = sections[i];
        const elt = section.domRef.current;
        const model = section.modelRef.current;
        if (!elt || !model) {
          console.warn('Either dom or model is missing in scroll section check:', section);
          continue;
        }

        const { top } = elt.getBoundingClientRect();
        const shouldFocusSection = top <= heightToFocusSection;
        if (shouldFocusSection) {
          setCurrentScrollSection(model);
          return;
        }
      }

      // If no section found from the second, the first section is set by default.
      const firstModel = sections[0]?.modelRef.current;
      if (firstModel) {
        setCurrentScrollSection(firstModel);
        return;
      }

      // No section found, it's a bug.
      console.warn('Bug: no section found when scrolling.');
    }, [sectionsRef]);
    const handleScrollDebounced = useDebouncedCallback(handleScroll, 300, { maxWait: 300 });

    // Listen to the scroll event and call the handler above.
    useEffect(() => {
      // In CSS, the overflow scroll is done on #__next. This is where the scroll event is listened to.
      // See #__next in globals.scss.
      // const scrollElement = document;
      const scrollElement = document.querySelector<HTMLElement>(frontConfig.rootElementSelector);
      if (!scrollElement) {
        throw new Error('Root element not found while loading ScrollSectionsProvider.');
      }
      scrollableElementRef.current = scrollElement as any;
      scrollElement.addEventListener('scroll', handleScrollDebounced);

      return () => {
        scrollElement.removeEventListener('scroll', handleScrollDebounced);
      };
    }, [handleScrollDebounced]);

    const sectionsHelpersRef = useRef<SectionsHelpers>({} as SectionsHelpers);
    const sectionsHelpers = useMemo(() => {
      const helpers = sectionsHelpersRef.current;
      helpers.sections = sectionsRef.current;
      helpers.setCurrentScrollSection = setCurrentScrollSection;
      helpers.forceCurrentScrollSection = forceCurrentScrollSection;
      helpers.unforceCurrentScrollSection = unforceCurrentScrollSection;
      helpers.checkCurrentScrollSection = handleScrollDebounced;
      return helpers;
    }, [forceCurrentScrollSection, handleScrollDebounced, unforceCurrentScrollSection]);

    return (
      <ScrollSectionsHelpersContext.Provider value={sectionsHelpers}>
        <CurrentScrollSectionContext.Provider value={currentScrollSection}>
          <CurrentScrollSectionForcedContext.Provider value={currentScrollSectionForced}>
            {children}
          </CurrentScrollSectionForcedContext.Provider>
        </CurrentScrollSectionContext.Provider>
      </ScrollSectionsHelpersContext.Provider>
    );
  },
);

export function useScrollSectionsHelpers() {
  return useContext(ScrollSectionsHelpersContext);
}

export function useCurrentScrollSection() {
  return useContext(CurrentScrollSectionContext);
}

export function useCurrentScrollSectionForced() {
  return useContext(CurrentScrollSectionForcedContext);
}

export interface RegisterScrollSectionProps {
  domRef: DomRef;
  model: ScrollSection;
}

// Component wrapping the useRegisterScrollSection hook if the hook needs to be used in a components that also renders in the server. To avoid a SSR warning about useLayoutEffect.
export const RegisterScrollSection: FC<RegisterScrollSectionProps> = function RegisterScrollSection(
  props,
) {
  return isBrowser ? <RegisterScrollSection2 {...props} /> : null;
};
const RegisterScrollSection2: FC<RegisterScrollSectionProps> = function RegisterScrollSection2(
  props,
) {
  const { domRef, model } = props;
  useRegisterScrollSection(domRef, model);
  return null;
};

// Use with all sections that should be registered as a scroll section
export function useRegisterScrollSection(domRef: DomRef, model: ScrollSection) {
  const { sections, setCurrentScrollSection } = useScrollSectionsHelpers();

  const modelRef = useRef(model);
  modelRef.current = model;

  useEffect(() => {
    const domAndModelToTrack: DomAndModelRefs = { domRef, modelRef };

    if (domRef.current && modelRef.current) {
      if (!sections.length) {
        // First section registered: initialize the current section
        setCurrentScrollSection(modelRef.current);
      }
      const i = sections.indexOf(domAndModelToTrack);
      if (i === -1) {
        sections.push(domAndModelToTrack);
      }
      // insertInSortedArray(sections, domAndModelToTrack, compareRef, true);
    }
    return () => {
      const i = sections.indexOf(domAndModelToTrack);
      if (i !== -1) {
        sections.splice(i, 1);
        if (!sections.length) {
          // Last section removed: unset the current section
          setCurrentScrollSection(undefined);
        }
      }
    };
  }, [domRef, sections, setCurrentScrollSection]);
}

// Implementation details

function compareRef<T extends DomAndModelRefs>(a: T, b: T) {
  if (!a.domRef.current || !b.domRef.current) throw new Error('A ref has a falsy current');
  const { top: topA } = a.domRef.current.getBoundingClientRect();
  const { top: topB } = b.domRef.current.getBoundingClientRect();
  return topA - topB;
}

// Let's keep the insert functions below for 2-3 weeks to ensure the above implementation is stable and doesn't need to sort while we insert, i.e. no performance issue.

// // Linear complexity, because binary search is not worth it with very small arrays.
// function insertInSortedArray<T extends object>(
//   sortedArray: T[],
//   newElement: T,
//   compare: (a: T, b: T) => number,
//   inPlace?: boolean,
// ): T[] {
//   if (inPlace) return insertInSortedArrayMutable(sortedArray, newElement, compare);
//   const result = [...sortedArray];
//   let i = 0;
//   while (i < result.length && compare(result[i], newElement) <= 0) {
//     i++;
//   }
//   result.splice(i, 0, newElement);
//   return result;
// }
//
// function insertInSortedArrayMutable<T extends object>(
//   sortedArray: T[],
//   newElement: T,
//   compare: (a: T, b: T) => number,
// ) {
//   let i = 0;
//   while (i < sortedArray.length && compare(sortedArray[i], newElement) <= 0) {
//     i++;
//   }
//   sortedArray.splice(i, 0, newElement);
//   return sortedArray;
// }
